(() => {
  const selectors = {
    viewport: '[data-carousel-viewport]',
    track: '[data-carousel-track]',
    slide: '[data-character-card]',
    prev: '[data-carousel-prev]',
    next: '[data-carousel-next]',
    open: '[data-open-card]',
    close: '[data-close-card]',
    overlay: '[data-overlay-close]',
    modal: '[data-modal]',
    modalCard: '[data-modal-card]',
    status: '[data-carousel-status]',
    cardBody: '.meet-characters__card-body'
  }

  class MeetCharacters extends HTMLElement {
    constructor() {
      super()

      this.currentIndex = 0
      this.originalCount = 0
      this.slideStep = 0
      this.isAnimating = false
      this.activeSlide = null
      this.activeTrigger = null
      this.scrollLocked = false

      this.handlePrev = this.handlePrev.bind(this)
      this.handleNext = this.handleNext.bind(this)
      this.handleTrackClick = this.handleTrackClick.bind(this)
      this.handleOverlayClick = this.handleOverlayClick.bind(this)
      this.handleViewportScroll = this.handleViewportScroll.bind(this)
      this.handleKeydown = this.handleKeydown.bind(this)
      this.handleTransitionEnd = this.handleTransitionEnd.bind(this)
      this.handleResize = this.handleResize.bind(this)
      this.handleBlockSelect = this.handleBlockSelect.bind(this)
      this.restoreModalLayer = this.restoreModalLayer.bind(this)
    }

    connectedCallback() {
      if (this.initialized) {
        return
      }

      this.initialized = true
      this.animationSpeed = Number(this.dataset.animationSpeed) || 320
      this.viewport = this.querySelector(selectors.viewport)
      this.track = this.querySelector(selectors.track)
      this.prevButton = this.querySelector(selectors.prev)
      this.nextButton = this.querySelector(selectors.next)
      this.overlay = this.querySelector(selectors.overlay)
      this.modal = this.querySelector(selectors.modal)
      this.modalCard = this.querySelector(selectors.modalCard)
      this.status = this.querySelector(selectors.status)

      if (!this.viewport || !this.track) {
        return
      }

      this.mountModalLayer()
      this.syncModalStyles()

      this.originalSlides = Array.from(this.track.querySelectorAll(selectors.slide))
      this.originalCount = this.originalSlides.length

      if (!this.originalCount) {
        return
      }

      this.setupLoop()
      this.attachEvents()
      this.observeResize()

      requestAnimationFrame(() => {
        this.setMeasurements()
        this.jumpToIndex(this.currentIndex)
        this.updateStatus()
      })
    }

    disconnectedCallback() {
      this.prevButton?.removeEventListener('click', this.handlePrev)
      this.nextButton?.removeEventListener('click', this.handleNext)
      this.track?.removeEventListener('click', this.handleTrackClick)
      this.track?.removeEventListener('transitionend', this.handleTransitionEnd)
      this.viewport?.removeEventListener('scroll', this.handleViewportScroll)
      this.overlay?.removeEventListener('click', this.handleOverlayClick)
      this.modalCard?.removeEventListener('click', this.handleTrackClick)
      document.removeEventListener('keydown', this.handleKeydown)
      document.removeEventListener('shopify:block:select', this.handleBlockSelect)
      window.removeEventListener('resize', this.handleResize)

      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
      }

      if (this.resizeTimer) {
        window.clearTimeout(this.resizeTimer)
      }

      if (this.transitionTimer) {
        window.clearTimeout(this.transitionTimer)
      }

      if (this.scrollEndTimer) {
        window.clearTimeout(this.scrollEndTimer)
      }

      this.closeActiveCard({ restoreFocus: false })
      this.restoreModalLayer()
    }

    setupLoop() {
      this.removeClones()

      if (this.originalCount <= 1) {
        this.currentIndex = 0
        this.updateButtons()
        return
      }

      const prependClones = this.originalSlides.map((slide) => this.createClone(slide)).reverse()
      const appendClones = this.originalSlides.map((slide) => this.createClone(slide))

      prependClones.forEach((clone) => {
        this.track.insertBefore(clone, this.track.firstChild)
      })

      appendClones.forEach((clone) => {
        this.track.appendChild(clone)
      })

      this.currentIndex = this.originalCount
      this.updateButtons()
    }

    removeClones() {
      this.track.querySelectorAll('[data-clone="true"]').forEach((clone) => clone.remove())
    }

    createClone(slide) {
      const clone = slide.cloneNode(true)

      clone.dataset.clone = 'true'
      clone.setAttribute('aria-hidden', 'true')
      clone.removeAttribute('id')
      clone.removeAttribute('data-block-id')

      Array.from(clone.attributes).forEach((attribute) => {
        if (attribute.name.startsWith('data-shopify-')) {
          clone.removeAttribute(attribute.name)
        }
      })

      clone.querySelectorAll('button').forEach((button) => {
        button.setAttribute('tabindex', '-1')
      })

      return clone
    }

    attachEvents() {
      this.prevButton?.addEventListener('click', this.handlePrev)
      this.nextButton?.addEventListener('click', this.handleNext)
      this.track.addEventListener('click', this.handleTrackClick)
      this.track.addEventListener('transitionend', this.handleTransitionEnd)
      this.viewport?.addEventListener('scroll', this.handleViewportScroll, { passive: true })
      this.overlay?.addEventListener('click', this.handleOverlayClick)
      this.modalCard?.addEventListener('click', this.handleTrackClick)
      document.addEventListener('keydown', this.handleKeydown)
      document.addEventListener('shopify:block:select', this.handleBlockSelect)
      window.addEventListener('resize', this.handleResize)
    }

    observeResize() {
      if (!('ResizeObserver' in window)) {
        return
      }

      this.resizeObserver = new ResizeObserver(() => {
        this.handleResize()
      })

      this.resizeObserver.observe(this.viewport)
    }

    mountModalLayer() {
      if (typeof document === 'undefined') {
        return
      }

      if (this.overlay) {
        if (!this.overlayOriginalParent) {
          this.overlayOriginalParent = this.overlay.parentNode
        }

        if (this.overlay.parentNode !== document.body) {
          document.body.appendChild(this.overlay)
        }
      }

      if (this.modal) {
        if (!this.modalOriginalParent) {
          this.modalOriginalParent = this.modal.parentNode
        }

        if (this.modal.parentNode !== document.body) {
          document.body.appendChild(this.modal)
        }
      }
    }

    restoreModalLayer() {
      if (typeof document === 'undefined') {
        return
      }

      this.overlay?.classList.remove('is-active')
      this.modal?.classList.remove('is-active')

      if (this.overlay && this.overlayOriginalParent && this.overlay.parentNode === document.body) {
        this.overlayOriginalParent.appendChild(this.overlay)
      }

      if (this.modal && this.modalOriginalParent && this.modal.parentNode === document.body) {
        this.modalOriginalParent.appendChild(this.modal)
      }
    }

    syncModalStyles() {
      const computedStyles = window.getComputedStyle(this)
      const variableNames = ['--meet-speed', '--meet-close-size']

      variableNames.forEach((variableName) => {
        const value = computedStyles.getPropertyValue(variableName)

        if (!value) {
          return
        }

        this.overlay?.style.setProperty(variableName, value)
        this.modal?.style.setProperty(variableName, value)
      })
    }

    handlePrev() {
      this.navigate(-1)
    }

    handleNext() {
      this.navigate(1)
    }

    navigate(direction) {
      if (this.originalCount <= 1 || this.isAnimating) {
        return
      }

      if (this.activeSlide) {
        this.closeActiveCard({ restoreFocus: false })
      }

      this.currentIndex += direction
      this.isAnimating = true

      this.applyPosition(true)
      this.transitionTimer = window.setTimeout(() => {
        this.finishTransition()
      }, this.animationSpeed + 80)
    }

    handleTrackClick(event) {
      const closeButton = event.target.closest(selectors.close)

      if (closeButton) {
        event.preventDefault()
        event.stopPropagation()
        this.closeActiveCard()
        return
      }

      const openButton = event.target.closest(selectors.open)

      if (!openButton) {
        return
      }

      event.preventDefault()

      const slide = openButton.closest(selectors.slide)

      if (!slide) {
        return
      }

      this.openCard(slide, openButton)
    }

    handleOverlayClick(event) {
      event.preventDefault()
      this.closeActiveCard()
    }

    handleViewportScroll() {
      if (!this.usesMobileScroll() || !this.slideStep || this.activeSlide) {
        return
      }

      if (this.scrollEndTimer) {
        window.clearTimeout(this.scrollEndTimer)
      }

      this.scrollEndTimer = window.setTimeout(() => {
        this.syncIndexFromScroll()
      }, 90)
    }

    handleKeydown(event) {
      if (event.key === 'Escape' && this.activeSlide) {
        event.preventDefault()
        this.closeActiveCard()
      }
    }

    handleTransitionEnd(event) {
      if (event.target !== this.track || event.propertyName !== 'transform') {
        return
      }

      this.finishTransition()
    }

    finishTransition() {
      if (!this.isAnimating) {
        return
      }

      if (this.transitionTimer) {
        window.clearTimeout(this.transitionTimer)
        this.transitionTimer = null
      }

      if (this.currentIndex >= this.originalCount * 2) {
        this.currentIndex -= this.originalCount
        this.applyPosition(false)
      } else if (this.currentIndex < this.originalCount) {
        this.currentIndex += this.originalCount
        this.applyPosition(false)
      }

      this.isAnimating = false
      this.updateStatus()
    }

    handleResize() {
      if (this.resizeTimer) {
        window.clearTimeout(this.resizeTimer)
      }

      this.resizeTimer = window.setTimeout(() => {
        this.isAnimating = false

        if (this.transitionTimer) {
          window.clearTimeout(this.transitionTimer)
          this.transitionTimer = null
        }

        this.setMeasurements()
        this.syncModalStyles()
        this.applyPosition(false)
      }, 60)
    }

    handleBlockSelect(event) {
      if (!this.contains(event.target)) {
        return
      }

      const slide = event.target.closest(selectors.slide)

      if (!slide || slide.dataset.clone === 'true') {
        return
      }

      if (this.originalCount > 1) {
        this.currentIndex = this.originalCount + Number(slide.dataset.sourceIndex)
        this.applyPosition(false)
      }

      this.closeActiveCard({ restoreFocus: false })
      slide.querySelector(selectors.open)?.focus()
    }

    readCssVar(name) {
      return parseFloat(window.getComputedStyle(this).getPropertyValue(name)) || 0
    }

    usesMobileScroll() {
      return window.innerWidth < 750
    }

    setMeasurements() {
      const styles = window.getComputedStyle(this.track)
      const gap = parseFloat(styles.columnGap || styles.gap || 0)
      const viewportWidth = this.viewport.getBoundingClientRect().width

      if (!viewportWidth) {
        return
      }

      let visibleCards = 3.5
      let visibleGaps = 3
      let baseWidth = this.readCssVar('--meet-card-width-desktop')
      let sideOffset = 0

      if (this.usesMobileScroll()) {
        visibleCards = 1.12
        visibleGaps = 1
        baseWidth = this.readCssVar('--meet-card-width-mobile')
      } else if (window.innerWidth < 990) {
        visibleCards = 2.35
        visibleGaps = 2
        baseWidth = Math.max(this.readCssVar('--meet-card-width-mobile'), this.readCssVar('--meet-card-width-desktop') * 0.78)
      }

      const calculatedWidth = (viewportWidth - gap * visibleGaps) / visibleCards
      const maxWidth = Math.max(0, viewportWidth - gap)
      const cardWidth = Math.max(0, Math.min(maxWidth, Math.max(baseWidth, calculatedWidth)))

      if (this.usesMobileScroll()) {
        sideOffset = Math.max(0, (viewportWidth - cardWidth) / 2)
      }

      this.style.setProperty('--meet-live-card-width', `${cardWidth}px`)
      this.style.setProperty('--meet-mobile-side-offset', `${sideOffset}px`)
      this.slideStep = cardWidth + gap
    }

    jumpToIndex(index) {
      this.currentIndex = index
      this.applyPosition(false)
    }

    applyPosition(animate) {
      if (!this.slideStep) {
        return
      }

      if (this.usesMobileScroll()) {
        const left = this.slideStep * this.currentIndex

        this.track.style.transitionDuration = '0ms'
        this.track.style.transform = 'translate3d(0, 0, 0)'

        if (animate) {
          this.viewport.scrollTo({ left, top: 0, behavior: 'smooth' })
        } else {
          this.viewport.scrollLeft = left
        }

        this.updateStatus()
        return
      }

      const offset = this.slideStep * this.currentIndex * -1

      this.track.style.transitionDuration = animate ? `${this.animationSpeed}ms` : '0ms'
      this.track.style.transform = `translate3d(${offset}px, 0, 0)`

      if (!animate) {
        this.track.getBoundingClientRect()
        requestAnimationFrame(() => {
          this.track.style.transitionDuration = `${this.animationSpeed}ms`
        })
      }

      this.updateStatus()
    }

    syncIndexFromScroll() {
      if (!this.usesMobileScroll() || !this.slideStep) {
        return
      }

      this.currentIndex = Math.round(this.viewport.scrollLeft / this.slideStep)

      if (this.currentIndex >= this.originalCount * 2) {
        this.currentIndex -= this.originalCount
        this.applyPosition(false)
      } else if (this.currentIndex < this.originalCount) {
        this.currentIndex += this.originalCount
        this.applyPosition(false)
      }

      this.updateStatus()
    }

    updateButtons() {
      const disabled = this.originalCount <= 1

      if (this.prevButton) {
        this.prevButton.disabled = disabled
      }

      if (this.nextButton) {
        this.nextButton.disabled = disabled
      }
    }

    updateStatus() {
      if (!this.status || !this.originalCount) {
        return
      }

      const visibleIndex = ((this.currentIndex % this.originalCount) + this.originalCount) % this.originalCount
      this.status.textContent = `Showing character ${visibleIndex + 1} of ${this.originalCount}`
    }

    openCard(slide, trigger) {
      if (!slide) {
        return
      }

      if (this.activeSlide === slide) {
        return
      }

      if (this.activeSlide) {
        this.closeActiveCard({ restoreFocus: false })
      }

      this.activeSlide = slide
      this.activeTrigger = trigger || slide.querySelector(selectors.open)

      this.renderModalCard(slide)
      this.classList.add('is-modal-open')
      this.overlay?.classList.add('is-active')
      this.modal?.classList.add('is-active')
      slide.querySelector(selectors.open)?.setAttribute('aria-expanded', 'true')
      this.modal?.setAttribute('aria-hidden', 'false')
      this.lockScroll()

      requestAnimationFrame(() => {
        this.modalCard?.querySelector(selectors.close)?.focus()
      })
    }

    closeActiveCard(options = {}) {
      const { restoreFocus = true } = options

      if (!this.activeSlide) {
        this.classList.remove('is-modal-open')
        this.overlay?.classList.remove('is-active')
        this.modal?.classList.remove('is-active')
        this.modal?.setAttribute('aria-hidden', 'true')
        this.unlockScroll()
        return
      }

      const slide = this.activeSlide
      const trigger = this.activeTrigger || slide.querySelector(selectors.open)

      slide.querySelector(selectors.open)?.setAttribute('aria-expanded', 'false')

      this.activeSlide = null
      this.activeTrigger = null
      this.classList.remove('is-modal-open')
      this.overlay?.classList.remove('is-active')
      this.modal?.classList.remove('is-active')
      this.modal?.setAttribute('aria-hidden', 'true')
      if (this.modalCard) {
        this.modalCard.innerHTML = ''
      }
      this.unlockScroll()

      if (restoreFocus && trigger?.isConnected) {
        requestAnimationFrame(() => {
          trigger.focus()
        })
      }
    }

    lockScroll() {
      if (this.scrollLocked) {
        return
      }

      const scrollTarget = this.modalCard?.querySelector(selectors.cardBody) || this.modalCard || this

      this.scrollLocked = true
      window.dispatchEvent(new CustomEvent('theme:scroll:lock', { detail: scrollTarget }))
    }

    unlockScroll() {
      if (!this.scrollLocked) {
        return
      }

      this.scrollLocked = false
      window.dispatchEvent(new CustomEvent('theme:scroll:unlock'))
    }

    renderModalCard(slide) {
      if (!this.modalCard) {
        return
      }

      const modalSlide = slide.cloneNode(true)

      modalSlide.classList.remove('meet-characters__slide')
      modalSlide.classList.add('meet-characters__modal-slide', 'is-flipped', 'is-focused')
      modalSlide.removeAttribute('data-character-card')
      modalSlide.removeAttribute('data-block-id')
      modalSlide.removeAttribute('data-source-index')
      modalSlide.removeAttribute('id')
      modalSlide.removeAttribute('aria-hidden')
      modalSlide.dataset.modalClone = 'true'

      Array.from(modalSlide.attributes).forEach((attribute) => {
        if (attribute.name.startsWith('data-shopify-')) {
          modalSlide.removeAttribute(attribute.name)
        }
      })

      this.modalCard.innerHTML = ''
      this.modalCard.appendChild(modalSlide)
      this.modalCard.querySelector(selectors.cardBody)?.scrollTo({ top: 0, behavior: 'auto' })
    }
  }

  if (!customElements.get('meet-characters')) {
    customElements.define('meet-characters', MeetCharacters)
  }
})()
